import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createActivity, updateActivity } from '@coparent/shared/src/services/activityService';
import { documentToAttachment, validateAttachment } from '@coparent/shared/src/services/attachmentService';
import { DAYS_OF_WEEK, DOCUMENT_CATEGORIES, ALLOWED_FILE_TYPES } from '@coparent/shared/src/utils/constants';
import { getChildIds } from '@coparent/shared/src/utils/childUtils';
import { uploadFileAttachment } from '../../utils/attachmentUpload';
import ChildMultiSelect from '../common/ChildMultiSelect';
import DocumentPickerModal from '../common/DocumentPickerModal';
import AttachmentView from '../common/AttachmentView';
import { HiX, HiPaperClip, HiFolderOpen } from 'react-icons/hi';
import toast from 'react-hot-toast';

const ACTIVITY_COLORS = [
  { hex: '#22c55e', name: 'Green' },
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#8b5cf6', name: 'Purple' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#ef4444', name: 'Red' },
  { hex: '#f59e0b', name: 'Amber' },
  { hex: '#06b6d4', name: 'Cyan' },
  { hex: '#f97316', name: 'Orange' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#84cc16', name: 'Lime' },
];

const ActivityForm = ({ onClose, activity }) => {
  const { currentUser } = useAuth();
  const { familyId, family, familyChildren, childrenMap, getAllParticipants } = useFamily();
  const [loading, setLoading] = useState(false);
  const [activityType, setActivityType] = useState(activity?.type === 'event' ? 'event' : 'activity');
  const [selectedDays, setSelectedDays] = useState(activity?.dayOfWeek || []);
  const [frequency, setFrequency] = useState(activity?.frequency || 'weekly');
  const [monthlyDayOfMonth, setMonthlyDayOfMonth] = useState(activity?.monthlyDayOfMonth || 1);
  const [color, setColor] = useState(activity?.color || '#22c55e');
  const [selectedChildIds, setSelectedChildIds] = useState(getChildIds(activity));
  const [attachments, setAttachments] = useState(activity?.attachments || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saveToDocs, setSaveToDocs] = useState(false);
  const [docCategory, setDocCategory] = useState('other');
  const [showDocPicker, setShowDocPicker] = useState(false);
  const isEditing = !!activity;

  const handleFileSelect = (files) => {
    const accepted = [];
    Array.from(files || []).forEach((file) => {
      const check = validateAttachment(file);
      if (!check.valid) {
        toast.error(`${file.name}: ${check.error}`);
        return;
      }
      accepted.push(file);
    });
    if (accepted.length) setPendingFiles((prev) => [...prev, ...accepted]);
  };

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: activity ? {
      name: activity.name || '',
      location: activity.location || '',
      address: activity.address || '',
      startTime: activity.startTime || '',
      endTime: activity.endTime || '',
      dropoffParent: activity.dropoffParentId || 'custody',
      pickupParent: activity.pickupParentId || 'custody',
      cost: activity.cost || '',
      paidBy: activity.paidBy || '',
      costSplitRatio: activity.costSplitRatio ?? 50,
      notes: activity.notes || '',
      biweeklyStartDate: activity?.biweeklyStartDate || '',
      startDate: activity?.startDate || '',
      endDate: activity?.endDate || '',
      eventDate: activity?.eventDate || '',
    } : {
      dropoffParent: 'custody',
      pickupParent: 'custody',
      costSplitRatio: 50,
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      eventDate: '',
    }
  });

  const toggleDay = (dayIndex) => {
    setSelectedDays(prev =>
      prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const onSubmit = async (data) => {
    if (activityType === 'activity' && frequency !== 'monthly' && selectedDays.length === 0) {
      toast.error('Select at least one day');
      return;
    }
    if (activityType === 'event' && !data.eventDate) {
      toast.error('Select a date for this event');
      return;
    }
    if (activityType === 'activity' && data.startDate && data.endDate && data.endDate < data.startDate) {
      toast.error('End date must be on or after the start date');
      return;
    }
    setLoading(true);
    try {
      const activityData = {
        name: data.name,
        type: activityType,
        childIds: selectedChildIds,
        location: data.location || null,
        address: data.address || null,
        dayOfWeek: activityType === 'activity' && frequency !== 'monthly' ? selectedDays : [],
        monthlyDayOfMonth: activityType === 'activity' && frequency === 'monthly' ? monthlyDayOfMonth : null,
        startTime: data.startTime || null,
        endTime: data.endTime || null,
        dropoffParentId: data.dropoffParent === 'custody' ? null : data.dropoffParent,
        pickupParentId: data.pickupParent === 'custody' ? null : data.pickupParent,
        usesCustodyDefault: data.dropoffParent === 'custody' && data.pickupParent === 'custody',
        color,
        frequency: activityType === 'activity' ? frequency : null,
        biweeklyStartDate: activityType === 'activity' && frequency === 'biweekly' ? (data.biweeklyStartDate || null) : null,
        startDate: activityType === 'activity' ? (data.startDate || new Date().toISOString().split('T')[0]) : null,
        endDate: activityType === 'activity' ? (data.endDate || null) : null,
        eventDate: activityType === 'event' ? data.eventDate : null,
        season: null,
        cost: data.cost ? parseFloat(data.cost) : null,
        paidBy: data.cost ? (data.paidBy || currentUser.uid) : null,
        costSplitRatio: data.cost ? parseInt(data.costSplitRatio) : null,
        notes: data.notes || null,
        attachments,
      };

      let activityId = activity?.id;
      if (isEditing) {
        await updateActivity(familyId, activityId, activityData);
      } else {
        const created = await createActivity(familyId, activityData);
        activityId = created.id;
      }

      // Upload any newly picked files now that we have the activity id, then
      // persist the merged attachment list.
      if (pendingFiles.length) {
        const uploaded = [];
        for (const file of pendingFiles) {
          const stored = await uploadFileAttachment(familyId, file, {
            scope: 'activity',
            parentId: activityId,
            saveToDocs,
            category: docCategory,
            childIds: selectedChildIds,
            currentUser,
          });
          uploaded.push(stored);
        }
        await updateActivity(familyId, activityId, { attachments: [...attachments, ...uploaded] });
      }

      toast.success(isEditing ? 'Activity updated!' : 'Activity added!');
      onClose();
    } catch (error) {
      toast.error(isEditing ? 'Failed to update activity' : 'Failed to add activity');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">{isEditing ? (activityType === 'event' ? 'Edit Event' : 'Edit Activity') : (activityType === 'event' ? 'Add Event' : 'Add Activity')}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Activity Name</label>
            <input
              {...register('name', { required: 'Required' })}
              type="text"
              className="input-base"
              placeholder="e.g., Soccer Practice"
            />
            {errors.name && <p className="mt-1.5 text-xs text-accent-600 font-medium">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Type</label>
            <div className="flex gap-2">
              {[
                { value: 'activity', label: 'Recurring Activity' },
                { value: 'event', label: 'One-off Event' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setActivityType(opt.value)}
                  className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all duration-200 ${
                    activityType === opt.value
                      ? 'bg-ink-800 text-white shadow-warm-sm'
                      : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Color</label>
            <div className="flex gap-2 flex-wrap">
              {ACTIVITY_COLORS.map(c => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setColor(c.hex)}
                  className="w-7 h-7 rounded-full transition-all duration-150 flex items-center justify-center"
                  style={{
                    backgroundColor: c.hex,
                    boxShadow: color === c.hex ? `0 0 0 2px #fff, 0 0 0 4px ${c.hex}` : 'none',
                    transform: color === c.hex ? 'scale(1.15)' : 'scale(1)',
                  }}
                  title={c.name}
                >
                  {color === c.hex && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {activityType === 'activity' ? (
            <>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Frequency</label>
                <div className="flex gap-2">
                  {[
                    { value: 'weekly', label: 'Every week' },
                    { value: 'biweekly', label: 'Every other week' },
                    { value: 'monthly', label: 'Once a month' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFrequency(opt.value)}
                      className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all duration-200 ${
                        frequency === opt.value
                          ? 'bg-ink-800 text-white shadow-warm-sm'
                          : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {frequency === 'monthly' ? (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Day of month</label>
                  <select
                    value={monthlyDayOfMonth}
                    onChange={(e) => setMonthlyDayOfMonth(parseInt(e.target.value))}
                    className="input-base"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <p className="text-xs text-ink-300 mt-1">The activity will repeat on this day each month</p>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Days</label>
                  <div className="flex gap-1.5">
                    {DAYS_OF_WEEK.map((day, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggleDay(i)}
                        className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all duration-200 ${
                          selectedDays.includes(i)
                            ? 'bg-ink-800 text-white shadow-warm-sm'
                            : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                        }`}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {frequency === 'biweekly' && (
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">First week starts on</label>
                  <input
                    {...register('biweeklyStartDate')}
                    type="date"
                    className="input-base"
                  />
                  <p className="text-xs text-ink-300 mt-1">Pick a date in the first week this activity occurs</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Start date</label>
                <input
                  {...register('startDate')}
                  type="date"
                  className="input-base"
                />
                <p className="text-xs text-ink-300 mt-1">The activity won't appear on the calendar before this date</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">End date (optional)</label>
                <input
                  {...register('endDate')}
                  type="date"
                  className="input-base"
                />
                <p className="text-xs text-ink-300 mt-1">Leave blank to repeat indefinitely</p>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Date</label>
              <input
                {...register('eventDate')}
                type="date"
                className="input-base"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Start Time</label>
              <input
                {...register('startTime')}
                type="time"
                className="input-base"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">End Time</label>
              <input
                {...register('endTime')}
                type="time"
                className="input-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Location</label>
            <input
              {...register('location')}
              type="text"
              className="input-base"
              placeholder="e.g., City Park Field 3"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Notes</label>
            <textarea
              {...register('notes')}
              rows={3}
              className="input-base resize-y"
              placeholder="e.g., Zoom link, instructions, what to bring…"
            />
          </div>

          {familyChildren.length > 0 && (
            <ChildMultiSelect
              familyChildren={familyChildren}
              childrenMap={childrenMap}
              selectedChildIds={selectedChildIds}
              onChange={setSelectedChildIds}
            />
          )}

          {family && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Drop-off</label>
                <select
                  {...register('dropoffParent')}
                  className="input-base"
                >
                  <option value="custody">Whoever has custody</option>
                  {getAllParticipants().map(p => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}{p.role === 'household_member' ? ' (household)' : ''}
                    </option>
                  ))}
                  <option value="self">Self arrival (no drop-off)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Pick-up</label>
                <select
                  {...register('pickupParent')}
                  className="input-base"
                >
                  <option value="custody">Whoever has custody</option>
                  {getAllParticipants().map(p => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}{p.role === 'household_member' ? ' (household)' : ''}
                    </option>
                  ))}
                  <option value="self">Self dismissal (no pick-up)</option>
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Cost per session (optional)</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300 font-semibold">$</span>
              <input
                {...register('cost')}
                type="number"
                step="0.01"
                className="input-base pl-8"
                placeholder="0.00"
              />
            </div>
            {watch('cost') && family && (
              <p className="text-xs text-ink-300 mt-1.5">An expense will be logged automatically each time this activity occurs.</p>
            )}
          </div>

          {watch('cost') && family && (
            <>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Paid by</label>
                <select
                  {...register('paidBy')}
                  className="input-base"
                >
                  {getAllParticipants().map(p => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}{p.role === 'household_member' ? ' (household)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">
                  Split — other parent pays
                </label>
                <div className="flex items-center gap-3">
                  <input
                    {...register('costSplitRatio')}
                    type="range"
                    min="0"
                    max="100"
                    className="flex-1 accent-ink-800"
                  />
                  <span className="text-sm font-bold text-ink-700 w-12 text-right">
                    {watch('costSplitRatio')}%
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Attachments */}
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Attachments</label>
            <div className="flex gap-2 mb-3">
              <label className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-xl border border-dashed border-surface-300 text-ink-600 hover:bg-surface-50 cursor-pointer transition-colors">
                <HiPaperClip /> Attach file
                <input
                  type="file"
                  multiple
                  accept={Object.keys(ALLOWED_FILE_TYPES).join(',')}
                  className="hidden"
                  onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
                />
              </label>
              <button
                type="button"
                onClick={() => setShowDocPicker(true)}
                className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-xl border border-dashed border-surface-300 text-ink-600 hover:bg-surface-50 transition-colors"
              >
                <HiFolderOpen /> From Documents
              </button>
            </div>

            {(attachments.length > 0 || pendingFiles.length > 0) && (
              <div className="flex flex-wrap gap-3 mb-3">
                {attachments.map((att, i) => (
                  <AttachmentView
                    key={att.storagePath || `att-${i}`}
                    attachment={att}
                    onRemove={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                ))}
                {pendingFiles.map((file, i) => (
                  <AttachmentView
                    key={`pending-${i}`}
                    attachment={{
                      fileName: file.name,
                      fileType: file.type,
                      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
                    }}
                    onRemove={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                ))}
              </div>
            )}

            {pendingFiles.length > 0 && (
              <div>
                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={saveToDocs}
                    onChange={(e) => setSaveToDocs(e.target.checked)}
                    className="rounded border-surface-300 text-accent-500 focus:ring-accent-400"
                  />
                  Also save uploads to Documents
                </label>
                {saveToDocs && (
                  <select
                    value={docCategory}
                    onChange={(e) => setDocCategory(e.target.value)}
                    className="input-base mt-2"
                  >
                    {DOCUMENT_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
            >
              {loading ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : (activityType === 'event' ? 'Add Event' : 'Add Activity'))}
            </button>
          </div>
        </form>
      </div>

      {showDocPicker && (
        <DocumentPickerModal
          familyId={familyId}
          onClose={() => setShowDocPicker(false)}
          onSelect={(document) => setAttachments((prev) => [...prev, documentToAttachment(document)])}
        />
      )}
    </div>
  );
};

export default ActivityForm;
